import { type ReactNode, useCallback, useRef, useState } from 'react'

import { Popover, PopoverTrigger } from '@/components/ui/popover'

import type { Notification, NotificationClickHandler } from './notification.interface'
import { NotificationPopoverContent } from './notification-popover-content.atom'
import { NotificationTriggerButton } from './notification-trigger-button.atom'
import { useNotificationKeyboard } from './use-notification-keyboard.hook'

export interface NotificationCenterProps {
  notificationList: Notification[]
  onNotificationClick?: NotificationClickHandler
  onMarkAllRead?: () => void
  className?: string
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  notificationList,
  onNotificationClick,
  onMarkAllRead,
  className,
}): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const [focusedIndex, setFocusedIndex] = useState(-1)

  const unreadCount = notificationList.filter((n) => !n.isRead).length
  const hasUnread = unreadCount > 0

  const handleNotificationClick = useCallback(
    (notification: Notification) => {
      onNotificationClick?.(notification)
      setIsOpen(false)
    },
    [onNotificationClick]
  )

  const handleKeyDown = useNotificationKeyboard({
    notificationCount: notificationList.length,
    focusedIndex,
    setFocusedIndex,
    itemRefs,
  })

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    if (!open) setFocusedIndex(-1)
  }, [])

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <NotificationTriggerButton
          hasUnread={hasUnread}
          unreadCount={unreadCount}
          className={className}
        />
      </PopoverTrigger>
      <NotificationPopoverContent
        notificationList={notificationList}
        hasUnread={hasUnread}
        onMarkAllRead={onMarkAllRead}
        onNotificationClick={handleNotificationClick}
        onKeyDown={handleKeyDown}
        itemRefs={itemRefs}
      />
    </Popover>
  )
}

NotificationCenter.displayName = 'NotificationCenter'
